import '../../../sass/alpha-modules/optional/_range-slider.scss'

import AlphaHelpers from '../../abstracts/classes/alpha-helpers'
import AlphaModule from '../../abstracts/classes/alpha-module'
import NoUiSlider from 'nouislider/dist/nouislider.min'
import assign from 'lodash-es/assign'
import cloneDeep from 'lodash-es/cloneDeep'
import wNumb from 'wnumb'

export default class RangeSlider extends AlphaModule {
  getName() {
    return 'rangeSlider'
  }

  getDefaultConfig() {
    const attrName = AlphaHelpers.getAlphaAttributeName('required-modules')

    return {
      sliderSelector: `[${attrName}*="${this.getName()}"]`,
      noUiSliderConfig: {
        // eslint-disable-next-line no-magic-numbers
        start: [0, 4],
        range: {
          min: 0,
          max: 4
        },
        step: 1,
        connect: true,
        format: {
          from: (formattedValue) => Number(formattedValue),
          to: (numericValue) => Math.round(numericValue)
        },
        pips: {
          mode: 'range',
          density: 90
        }
      },
      wNumbConfig: {
        decimals: 0,
        mark: ',',
        thousand: ' '
      }
    }
  }

  _onLoad() {
    super._onLoad()

    this._initSliders()
  }

  _initSliders() {
    const config = this.getConfig()
    const sliders = AlphaHelpers.smartQuerySelectorAll(config.sliderSelector)
    sliders.forEach((sliderEl) => {
      // get default noUiSlider config or override it with inline one
      let noUiSliderConfig = cloneDeep(config.noUiSliderConfig)
      const noUiSliderConfigAttrName = AlphaHelpers.getAlphaAttributeName('range-slider-config')
      const customNoUiSliderConfig = AlphaHelpers.getParsedJsonAttribute(sliderEl, noUiSliderConfigAttrName)
      if (customNoUiSliderConfig) {
        noUiSliderConfig = assign(noUiSliderConfig, customNoUiSliderConfig)
      }

      // get default wNumb config and override it with inline one
      let wNumbConfig = cloneDeep(config.wNumbConfig)
      const wNumbConfigAttrName = AlphaHelpers.getAlphaAttributeName('w-numb-config')
      const customWNumbConfig = AlphaHelpers.getParsedJsonAttribute(sliderEl, wNumbConfigAttrName)
      if (customWNumbConfig) {
        wNumbConfig = assign(wNumbConfig, customWNumbConfig)
      }

      if (typeof noUiSliderConfig.format === 'undefined') {
        noUiSliderConfig.format = config.noUiSliderConfig.format
      }

      if (typeof noUiSliderConfig.tooltips === 'undefined') {
        noUiSliderConfig.tooltips = wNumb(wNumbConfig)
      }

      if (typeof noUiSliderConfig.pips !== 'undefined') {
        noUiSliderConfig.pips.format = wNumb(wNumbConfig)
      }

      // initialize NoUiSlider with provided config
      let isInit = true
      const inputs = AlphaHelpers.smartQuerySelectorAll('input[type="number"]', sliderEl.parentElement)
      NoUiSlider.create(sliderEl, noUiSliderConfig)

      // listen to slider range changes and store values in linked inputs
      sliderEl.noUiSlider.on('update', (values, handle) => {
        if (!isInit) {
          inputs.forEach((inputEl, index) => {
            const value = values[index]

            if (typeof value !== 'undefined') {
              if (!inputEl.hasAttribute('value') || handle === index) {
                inputEl.value = value
              }

              // enable / disable fields values submit (depending on value being set to its initial value or not)
              // this code allows us to ignore unchanged sliders upon form submit
              const isInitialRangeValues = this._isInitialRangeValues(sliderEl, inputEl, noUiSliderConfig)
              this._enableFieldValueSubmit(inputEl, !isInitialRangeValues)

              if (isInitialRangeValues) {
                const otherInputEl = this._getOtherInputElement(sliderEl, inputEl)
                this._enableFieldValueSubmit(otherInputEl, false)
                return false
              }
            }

            return true
          })
        } else if (handle) {
          isInit = false
        }

        // synchronize fields min / max attributes with current controls values
        inputs.forEach((inputEl, index) => {
          const value = values[index]
          if (typeof value !== 'undefined') {
            const otherInputEl = this._getOtherInputElement(sliderEl, inputEl)
            if (otherInputEl) {
              const attrName = this._getRangeAttributeNameFromIndex(index)
              otherInputEl.setAttribute(attrName, value.toString())
            }
          }
        })
      })

      inputs.forEach((inputEl, index) => {
        // update slider values on input changes
        inputEl.addEventListener('change', () => {
          const attrName = this._getRangeAttributeNameFromIndex(index)
          const otherAttrName = this._getRangeAttributeNameFromIndex(index, true)
          const rangeValue = parseInt(inputEl.getAttribute(otherAttrName), 10)
          const limit = noUiSliderConfig.range[attrName]

          // ensure that passed value is between min & max
          let value = Math[otherAttrName](inputEl.value, limit)
          switch (attrName) {
            case 'min':
              if (value > rangeValue) {
                value = rangeValue
              }
              break
            case 'max':
              if (value < rangeValue) {
                value = rangeValue
              }
              break
            default:
              break
          }

          inputEl.value = value

          // disable fields values submit when value is reset to min / max
          if (this._isInitialRangeValues(sliderEl, inputEl, noUiSliderConfig)) {
            this._enableFieldsValuesSubmit(sliderEl, inputEl, false)
          }

          // update slider value accordingly
          sliderEl.noUiSlider.setHandle(index, inputEl.value, false, true)
        })

        // prevent submitting form when pressing "Enter", instead just blur input
        inputEl.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            inputEl.blur()
          }
        })
      })
    })
  }

  _getOtherInputElement(sliderEl, inputEl) {
    return sliderEl.parentElement.querySelector(`[type="number"]:not([name="${inputEl.name}"])`)
  }

  _getRangeAttributeNameFromIndex(index, reversed = false) {
    const attributes = ['min', 'max']
    const reversedInt = reversed ? 1 : 0
    let realIndex = index + reversedInt
    switch (index) {
      case 1:
        realIndex = index - reversedInt
        break
      default:
        break
    }

    return attributes[realIndex] || attributes[0]
  }

  _enableFieldsValuesSubmit(sliderEl, inputEl, enable = true) {
    const otherInputEl = this._getOtherInputElement(sliderEl, inputEl)
    this._enableFieldValueSubmit(inputEl, enable)
    this._enableFieldValueSubmit(otherInputEl, enable)
  }

  _enableFieldValueSubmit(inputEl, enable = true) {
    const nameAttr = 'name'
    const dataNameAttr = 'data-name'

    if (enable) {
      if (!inputEl.hasAttribute(nameAttr) && inputEl.hasAttribute(dataNameAttr)) {
        inputEl.name = inputEl.getAttribute('data-name')
      }
    } else {
      if (inputEl.hasAttribute(nameAttr)) {
        inputEl.setAttribute(dataNameAttr, inputEl.name)
      }

      inputEl.removeAttribute(nameAttr)
    }
  }

  _isInitialRangeValues(sliderEl, inputEl, noUiSliderConfig) {
    const otherInputEl = this._getOtherInputElement(sliderEl, inputEl)
    const values = [inputEl.value, otherInputEl.value].map((str) => parseInt(str, 10)).sort()

    return values[0] === noUiSliderConfig.range.min && values[1] === noUiSliderConfig.range.max
  }
}
